import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { useUID } from 'dibs-uid';
import ResponsiveImage from '../ResponsiveImage';
import classnames from 'classnames';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { trackModuleLocation } from '../helpers/moduleLocationTracking';

import styles from './HpRespSkinnyBannerModule.scss';

import { HpRespSkinnyBannerModule_componentModule } from './__generated__/HpRespSkinnyBannerModule_componentModule.graphql';

type BannerMessages = NonNullable<
    NonNullable<HpRespSkinnyBannerModule_componentModule['messages']>
>;
type LinkData = NonNullable<BannerMessages>['linkData'];

type Props = {
    componentModule: HpRespSkinnyBannerModule_componentModule;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
};

const HpRespSkinnyBannerModule: FC<Props> = ({
    componentModule,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
}) => {
    const uid = `skinnyBanner-${useUID() || undefined}`;

    const messages = componentModule?.messages;

    if (!messages?.linkData) {
        return null;
    }

    const imgSrc = messages?.imageUrl || messages?.mobileImageUrl || '';
    const imageCopy = messages?.imageCopy || undefined;
    const {
        isFullBleedImage,
        imageHeight,
        imageWidth,
        mobileImageHeight,
        mobileImageWidth,
        cta,
        linkData,
    } = messages || {};

    const promoTrackingObject = buildPromoTrackingObject({
        item: messages,
        id: 'skinny-banner',
        name: 'Skinny Banner',
    });

    const handleClick = (): void => {
        trackPromoClick(promoTrackingObject);
        trackModuleLocation({
            label: 'skinny banner module',
            moduleIndex,
            totalModules,
        });
    };

    return (
        <div
            className={classnames(styles.wrapper, {
                [styles.isFullBleed]: isFullBleedImage,
            })}
        >
            {imageCopy && (
                <span id={uid} className={styles.imageCopy}>
                    {imageCopy}
                </span>
            )}
            <SeoLink
                className={styles.link}
                dataTn="skinny-banner-link"
                linkData={linkData as LinkData}
                ariaLabel={cta || undefined}
                ariaDescribedBy={imageCopy ? uid : undefined}
                onClick={handleClick}
            >
                <HpLazyImageWrapper
                    useLazyLoad={useLazyLoadImages}
                    imageHeight={imageHeight || mobileImageHeight}
                    imageWidth={imageWidth || mobileImageWidth}
                    onImageLoaded={() => trackPromoImpressions([promoTrackingObject])}
                >
                    <ResponsiveImage
                        ariaHidden
                        className={classnames(styles.image, {
                            [styles.isFullBleed]: isFullBleedImage,
                        })}
                        src={imgSrc}
                        srcSetWidths={[400, 640, 960, 1280, 1440]}
                    />
                </HpLazyImageWrapper>
            </SeoLink>
        </div>
    );
};

export default createFragmentContainer(HpRespSkinnyBannerModule, {
    componentModule: graphql`
        fragment HpRespSkinnyBannerModule_componentModule on SkinnyBannerModule {
            messages(userType: $userType) {
                ... on SkinnyBannerModuleItemMessages @skip(if: $isMobile) {
                    imageUrl
                    imageHeight
                    imageWidth
                }
                ... on SkinnyBannerModuleItemMessages @include(if: $isMobile) {
                    mobileImageUrl
                    mobileImageHeight
                    mobileImageWidth
                }
                linkData {
                    path
                    ...SeoLink_linkData
                }
                cta
                imageCopy
                isFullBleedImage
            }
        }
    `,
});
